<form class="form-field-full" [formGroup]="botEditorForm">
  <div class="row m-b-24">
    <div class="form-field-container col-12">
      <label for="bot-name-input" class="form-field-label">Name</label>
      <mat-form-field class="gingr-input-wrapper w-full" appearance="outline">
        <input
          id="bot-name-input"
          matInput
          formControlName="name"
          placeholder="Agent name"
          required
        />
      </mat-form-field>
    </div>

    <div class="form-field-container col-12">
      <label for="bot-description-textarea" class="form-field-label">Description</label>
      <mat-form-field class="gingr-input-wrapper w-full" appearance="outline">
        <textarea
          id="bot-description-textarea"
          matInput
          placeholder="Agent description"
          formControlName="description"
          rows="3"
        ></textarea>
      </mat-form-field>
    </div>

    <div class="form-field-container col-12">
      <label for="bot-descriptionForAI-textarea" class="form-field-label">Description For AI</label>
      <mat-form-field class="gingr-input-wrapper w-full" appearance="outline">
        <textarea
          id="bot-descriptionForAI-textarea"
          matInput
          placeholder="Description For AI"
          formControlName="descriptionForAI"
          rows="3"
        ></textarea>
      </mat-form-field>
    </div>

    <div class="form-field-container col-12">
      <label for="bot-initial-prompt-textarea" class="form-field-label d-flex align-items-center"
        >Initial Prompt
        <mat-icon
          class="icon"
          matTooltip="The initial message sent when submitting without text input (e.g., file-only uploads)"
          >info</mat-icon
        ></label
      >
      <mat-form-field class="gingr-input-wrapper w-full" appearance="outline">
        <textarea
          id="bot-initial-prompt-textarea"
          matInput
          placeholder="Initial prompt for file uploads"
          formControlName="initialPrompt"
          rows="2"
        ></textarea>
      </mat-form-field>
    </div>

    <div class="form-field-container col-12">
      <label for="bot-instruction-input" class="form-field-label d-flex align-items-center"
        >Instruction
        <mat-icon
          class="icon"
          [matTooltip]="
            bot.type === 'quizBot'
              ? 'Editable. Instructions for the quiz agent.'
              : 'Read-only. This field is not used by the main bot. Instructions are configured on inner bots.'
          "
          >info</mat-icon
        ></label
      >
      <mat-form-field class="gingr-input-wrapper w-full" appearance="outline">
        <textarea
          id="bot-instruction-input"
          matInput
          formControlName="instruction"
          placeholder="Agent instructions"
          rows="3"
          [readonly]="bot.type !== 'quizBot'"
          [attr.aria-readonly]="bot.type !== 'quizBot'"
        ></textarea>
      </mat-form-field>
    </div>

    <div class="form-field-container col-12">
      <label for="bot-behaviour-input" class="form-field-label d-flex align-items-center"
        >Behaviour
        <mat-icon
          class="icon"
          [matTooltip]="
            bot.type === 'quizBot'
              ? 'Editable. Behaviour for the quiz agent.'
              : 'Read-only. This field is not used by the main bot. Behaviour is configured on inner bots.'
          "
          >info</mat-icon
        ></label
      >
      <mat-form-field class="gingr-input-wrapper w-full" appearance="outline">
        <textarea
          id="bot-behaviour-input"
          matInput
          formControlName="behaviour"
          placeholder="Agent behaviour"
          rows="3"
          [readonly]="bot.type !== 'quizBot'"
          [attr.aria-readonly]="bot.type !== 'quizBot'"
        ></textarea>
      </mat-form-field>
    </div>

    @if (['summaryBot', 'quizBot'].includes(bot.type)) {
      <div class="form-field-container col-12">
        <label for="bot-config-textarea" class="form-field-label d-flex align-items-center"
          >Config <mat-icon class="icon" matTooltip="{{ botConfig }}">info</mat-icon></label
        >
        <mat-form-field class="gingr-input-wrapper w-full" appearance="outline">
          <textarea
            id="bot-config-textarea"
            matInput
            placeholder="Config"
            formControlName="config"
            rows="3"
          ></textarea>
        </mat-form-field>
      </div>
    }

    <div class="form-field-container col-12">
      <label for="bot-model-select" class="form-field-label d-flex align-items-center"
        >Bot model</label
      >
      <mat-form-field class="gingr-input-wrapper w-full" appearance="outline">
        <mat-select id="bot-model-select" formControlName="bot_model" placeholder="Select model">
          @for (model of botsModels; track model.id) {
            <mat-option [value]="model.id">{{ model.name }}</mat-option>
          }
        </mat-select>
      </mat-form-field>
    </div>

    <div class="form-field-container col-12">
      <label for="bot-status-select" class="form-field-label d-flex align-items-center"
        >Bot Status</label
      >
      <mat-form-field class="gingr-input-wrapper w-full hide-sub" appearance="outline">
        <mat-select id="bot-status-select" formControlName="bot_status" placeholder="Select status">
          @for (status of botStatuses; track status) {
            <mat-option [value]="status">{{ status.replace('_', ' ') | titlecase }}</mat-option>
          }
        </mat-select>
      </mat-form-field>
    </div>

    @if (
      botsTypesForcedStatus.includes(bot.type) &&
      appGlobalSettings.getSettingsValue().master_bot_status
    ) {
      <app-notice
        [text]="
          'Master status (' +
          appGlobalSettings.getSettingsValue().master_bot_status +
          ') overrides status for this bot type.'
        "
      ></app-notice>
    }
  </div>

  @if (bot.type === 'quizBot') {
    <h3 class="mat-h3 m-t-24">Quiz bot extra configurations</h3>
    <div formGroupName="extraConfigurations" class="m-b-24">
      <div class="row" formGroupName="feedback">
        <div class="form-field-container col-12">
          <label for="feedback-behaviour-input" class="form-field-label">Feedback behaviour</label>
          <mat-form-field class="gingr-input-wrapper w-full" appearance="outline">
            <input
              id="feedback-behaviour-input"
              matInput
              placeholder="Feedback behaviour"
              formControlName="behaviour"
            />
          </mat-form-field>
        </div>

        <div class="form-field-container col-12">
          <label for="feedback-instruction-input" class="form-field-label"
            >Feedback instruction</label
          >
          <mat-form-field class="gingr-input-wrapper w-full" appearance="outline">
            <input
              id="feedback-instruction-input"
              matInput
              placeholder="Feedback instruction"
              formControlName="instruction"
            />
          </mat-form-field>
        </div>

        <div class="form-field-container col-12">
          <label for="feedback-config-input" class="form-field-label">Feedback config</label>
          <mat-form-field class="gingr-input-wrapper w-full" appearance="outline">
            <input
              id="feedback-config-input"
              matInput
              placeholder="Feedback config"
              formControlName="config"
            />
          </mat-form-field>
        </div>
      </div>

      <div formGroupName="initMessage">
        <div class="form-field-container">
          <label for="init-message-behaviour-input" class="form-field-label"
            >Init message behaviour</label
          >
          <mat-form-field class="gingr-input-wrapper w-full" appearance="outline">
            <input
              id="init-message-behaviour-input"
              matInput
              placeholder="Init message behaviour"
              formControlName="behaviour"
            />
          </mat-form-field>
        </div>

        <div class="form-field-container">
          <label for="init-message-instruction-input" class="form-field-label"
            >Init message instruction</label
          >
          <mat-form-field class="gingr-input-wrapper w-full" appearance="outline">
            <input
              id="init-message-instruction-input"
              matInput
              placeholder="Init message instruction"
              formControlName="instruction"
            />
          </mat-form-field>
        </div>

        <div class="form-field-container">
          <label for="init-message-config-input" class="form-field-label"
            >Init message config</label
          >
          <mat-form-field class="gingr-input-wrapper w-full" appearance="outline">
            <input
              id="init-message-config-input"
              matInput
              placeholder="Init message config"
              formControlName="config"
            />
          </mat-form-field>
        </div>
      </div>

      <div formGroupName="secondRun">
        <div class="form-field-container">
          <label for="second-run-config-input" class="form-field-label">Second run config</label>
          <mat-form-field class="gingr-input-wrapper w-full" appearance="outline">
            <input
              id="second-run-config-input"
              matInput
              placeholder="Second run config"
              formControlName="config"
            />
          </mat-form-field>
        </div>
      </div>
    </div>
  }

  <div class="d-flex justify-content-between flex-wrap">
    @if (bot.type !== 'quizBot') {
      <div>
        <div class="form-field-container">
          <label class="form-field-label m-l-4 d-flex align-items-center"
            >Materials
            <mat-icon class="icon" matTooltip="{{ materialsInfo }}">info</mat-icon></label
          >
          <div class="d-flex option-box m-b-24">
            <mat-slider
              class="gingr-input-wrapper materials-input"
              min="0"
              max="10"
              step="1"
              discrete
            >
              <input matInput matSliderThumb formControlName="maximum_resources" />
            </mat-slider>
            <div class="d-flex m-t-12">
              <mat-slide-toggle
                formControlName="display_resources"
                class="m-l-16"
                matTooltip="enable/disable materials"
              />
            </div>
          </div>
        </div>
      </div>
    }

    <div>
      <div class="form-field-container">
        <label class="form-field-label m-l-4 d-flex align-items-center"
          >Number of publications
          <mat-icon class="icon" matTooltip="{{ publicationsCountInfo }}">info</mat-icon></label
        >
        <div class="d-flex option-box m-b-24">
          <mat-slider
            class="gingr-input-wrapper materials-input"
            min="0"
            max="{{ MAX_PUBLICATIONS_LIMIT }}"
            step="1"
            discrete
          >
            <input matInput matSliderThumb formControlName="maximum_publications" />
          </mat-slider>
          <div class="d-flex m-t-12">
            <mat-slide-toggle
              formControlName="use_all_publications"
              class="m-l-16"
              matTooltip="use all publications"
            />
          </div>
        </div>
      </div>
    </div>
  </div>

  @if (bot.type === 'botCreator') {
    <app-inner-bots [bot]="bot"></app-inner-bots>
  }

  <app-talking-avatar-settings [bot]="bot"></app-talking-avatar-settings>

  <h3 class="mat-h3 m-t-48">Assign Projects</h3>
  <div class="form-field-container col-12 m-b-24">
    <label for="bot-projects-select" class="form-field-label">Assign Projects:</label>
    <ng-select
      id="bot-projects-select"
      formControlName="allowedProjects"
      [items]="availableProjects"
      bindLabel="name"
      bindValue="_id"
      placeholder="Select projects"
      [multiple]="true"
      [searchable]="true"
      [clearable]="true"
      [closeOnSelect]="false"
      class="ng-select"
    ></ng-select>
  </div>

  <h3 class="mat-h3 m-t-48">Assign Folders</h3>
  <div class="form-field-container col-12 m-b-24">
    <label for="bot-folders-select" class="form-field-label">Assign Folders:</label>
    <ng-select
      id="bot-folders-select"
      formControlName="allowedFolders"
      [items]="availableFolders"
      bindLabel="name"
      bindValue="_id"
      placeholder="Select folders"
      [multiple]="true"
      [searchable]="true"
      [clearable]="true"
      [closeOnSelect]="false"
      class="ng-select"
    ></ng-select>
  </div>

  <h3 class="mat-h3 m-t-48">Context Assignments</h3>
  <div class="context-assignments-section m-b-24">
    <p class="helper-text m-b-16">
      Assign context groups or items to this bot. Static assignments are always loaded, Dynamic
      assignments use similarity search, and Tool assignments let the AI search on-demand.
    </p>
    <!-- Warning banner for static assignments -->
    @if (getStaticWarningLevel() !== 'none') {
      <div class="warning-banner m-b-16" [ngClass]="'warning-' + getStaticWarningLevel()">
        <mat-icon>warning</mat-icon>
        <span>{{ getStaticWarningMessage() }}</span>
      </div>
    }

    <button
      type="button"
      mat-raised-button
      color="primary"
      (click)="addContextAssignment()"
      class="m-b-16"
    >
      <mat-icon>add</mat-icon> Add Context Assignment
    </button>
    <div formArrayName="contextAssignments">
      @for (assignment of contextAssignmentsArray.controls; track $index; let i = $index) {
        <div [formGroupName]="i" class="context-assignment-card m-b-16">
          <div class="d-flex justify-content-between align-items-center m-b-8">
            <span class="assignment-label">Assignment {{ i + 1 }}</span>
            <button
              type="button"
              mat-icon-button
              color="warn"
              (click)="removeContextAssignment(i)"
              matTooltip="Remove assignment"
            >
              <mat-icon>delete</mat-icon>
            </button>
          </div>

          <div class="row">
            <div class="form-field-container col-4">
              <label class="form-field-label">Target Type</label>
              <mat-form-field class="gingr-input-wrapper w-full" appearance="outline">
                <mat-select
                  formControlName="targetType"
                  placeholder="Select type"
                  (selectionChange)="onTargetTypeChange(i)"
                >
                  <mat-option value="group">Group</mat-option>
                  <mat-option value="item">Item</mat-option>
                </mat-select>
              </mat-form-field>
            </div>

            <div class="form-field-container col-4">
              <label class="form-field-label">Target</label>
              <!-- Group selector -->
              @if (getContextAssignmentGroup(i).get('targetType')?.value === 'group') {
                <ng-select
                  formControlName="targetId"
                  [items]="availableContextGroups"
                  bindLabel="name"
                  bindValue="_id"
                  placeholder="Select group"
                  [searchable]="true"
                  [clearable]="true"
                  [virtualScroll]="true"
                  (change)="onAssignmentChange()"
                  class="ng-select ng-select-form-field"
                >
                  <ng-template ng-option-tmp let-item="item">
                    @if (item.itemCount !== undefined) {
                      <div class="select-option" [title]="item.name">
                        {{ item.name }} <span class="item-count">({{ item.itemCount }} items)</span>
                      </div>
                    }
                    ;
                  </ng-template>
                </ng-select>
              }

              <!-- Item selector -->
              @if (getContextAssignmentGroup(i).get('targetType')?.value === 'item') {
                <ng-select
                  formControlName="targetId"
                  [items]="availableContextItems"
                  bindLabel="question"
                  bindValue="_id"
                  placeholder="Search items..."
                  [searchable]="true"
                  [clearable]="true"
                  [virtualScroll]="true"
                  (change)="onAssignmentChange()"
                  class="ng-select ng-select-form-field"
                >
                  <ng-template ng-option-tmp let-item="item">
                    <div class="select-option" [title]="item.question">
                      {{ item.question | slice: 0 : 60
                      }}{{ item.question.length > 60 ? '...' : '' }}
                    </div>
                  </ng-template>
                </ng-select>
              }
            </div>

            <div class="form-field-container col-4">
              <label class="form-field-label d-flex align-items-center"
                >Assignment Type
                <mat-icon
                  class="info-icon m-l-4"
                  matTooltip="Static: Always loaded in prompt. Dynamic: Loaded via similarity search. Tool: AI can search on-demand. Forced: Always loaded and enforced."
                  >info</mat-icon
                ></label
              >
              <mat-form-field class="gingr-input-wrapper w-full" appearance="outline">
                <mat-select
                  formControlName="assignmentType"
                  placeholder="Select type"
                  (selectionChange)="onAssignmentChange()"
                >
                  <mat-option value="static">Static (Always loaded)</mat-option>
                  <mat-option value="dynamic">Dynamic (Similarity search)</mat-option>
                  <mat-option value="tool">Tool (AI search)</mat-option>
                  <mat-option value="forced">Forced (Always enforced)</mat-option>
                </mat-select>
              </mat-form-field>
            </div>
          </div>
        </div>
      }
    </div>
  </div>

  <h3 class="mat-h3 m-t-48">Other</h3>
  <div class="form-field-container col-12 m-b-24">
    <label for="bot-reference-mode-select" class="form-field-label d-flex align-items-center"
      >Reference mode
      <mat-icon
        class="icon"
        matTooltip="Choose whether answers show the source file, the nested reference, or both."
        >info</mat-icon
      ></label
    >
    <mat-form-field class="gingr-input-wrapper w-full" appearance="outline">
      <mat-select id="bot-reference-mode-select" formControlName="referenceMode">
        @for (option of referenceModeOptions; track option.value) {
          <mat-option [value]="option.value">
            <div>{{ option.label }}</div>
            <small>{{ option.description }}</small>
          </mat-option>
        }
      </mat-select>
    </mat-form-field>
  </div>
  <div class="d-flex flex-col">
    <mat-checkbox formControlName="showOnDashboard">Show on home page</mat-checkbox>
    <mat-checkbox formControlName="useOwnKnowledge">Use own knowledge</mat-checkbox>
    <mat-checkbox formControlName="autoDeleteEnabled">Auto Delete Enabled</mat-checkbox>
  </div>

  <div class="form-field-container col-12 m-t-24">
    <label for="bot-reference-check-prompt-textarea" class="form-field-label"
      >Reference Check Prompt</label
    >
    <mat-form-field class="gingr-input-wrapper w-full" appearance="outline">
      <textarea
        id="bot-reference-check-prompt-textarea"
        matInput
        placeholder="Reference Check Prompt"
        formControlName="referenceCheckPrompt"
        rows="3"
      ></textarea>
    </mat-form-field>
  </div>

  <h3 class="mat-h3 m-t-48">UI Configuration</h3>
  <div formGroupName="uiConfiguration">
    <div formGroupName="homepage" class="m-b-24">
      <h4 class="mat-h4">Homepage Settings</h4>
      <div class="row">
        <div class="form-field-container col-6">
          <mat-checkbox formControlName="showButton">Show Button on Tile</mat-checkbox>
        </div>
        <div class="form-field-container col-6">
          <label for="homepage-button-name" class="form-field-label">Button Name</label>
          <mat-form-field class="gingr-input-wrapper w-full" appearance="outline">
            <input
              id="homepage-button-name"
              matInput
              formControlName="buttonName"
              placeholder="e.g., Start Chat"
            />
          </mat-form-field>
        </div>
      </div>
    </div>

    <div class="m-b-24">
      <h4 class="mat-h4">Input Fields</h4>
      <div class="m-b-16">
        @if (inputFieldsArray.length === 0) {
          <p class="helper-text">
            Add up to 3 input fields (one of each type: Textarea, Input1, Input2)
          </p>
        }
        @if (inputFieldsArray.length > 0 && inputFieldsArray.length < 3) {
          <p class="helper-text">You can add {{ 3 - inputFieldsArray.length }} more field(s)</p>
        }
        <button
          type="button"
          mat-raised-button
          color="primary"
          (click)="addInputField()"
          [disabled]="inputFieldsArray.length >= 3"
        >
          Add Input Field
        </button>
      </div>

      <div formArrayName="inputFields">
        @for (field of inputFieldsArray.controls; track $index; let i = $index) {
          <div [formGroupName]="i" class="input-field-card m-b-24">
            <div class="d-flex justify-content-between align-items-center m-b-16">
              <h5 class="mat-h5">Field {{ i + 1 }}</h5>
              <div class="d-flex gap-8">
                <button
                  type="button"
                  mat-icon-button
                  (click)="moveInputFieldUp(i)"
                  [disabled]="i === 0"
                  matTooltip="Move up"
                >
                  <mat-icon>arrow_upward</mat-icon>
                </button>
                <button
                  type="button"
                  mat-icon-button
                  (click)="moveInputFieldDown(i)"
                  [disabled]="i === inputFieldsArray.length - 1"
                  matTooltip="Move down"
                >
                  <mat-icon>arrow_downward</mat-icon>
                </button>
                <button
                  type="button"
                  mat-icon-button
                  color="warn"
                  (click)="removeInputField(i)"
                  matTooltip="Remove field"
                >
                  <mat-icon>delete</mat-icon>
                </button>
              </div>
            </div>

            <div class="row">
              <div class="form-field-container col-4">
                <label class="form-field-label">Field Type</label>
                <mat-form-field class="gingr-input-wrapper w-full" appearance="outline">
                  <mat-select formControlName="type" placeholder="Select type">
                    <mat-option
                      value="input1"
                      [disabled]="
                        hasFieldType('input1') &&
                        getInputFieldGroup(i).get('type')?.value !== 'input1'
                      "
                      >Input1 (Primary Upload)</mat-option
                    >
                    <mat-option
                      value="textarea"
                      [disabled]="
                        hasFieldType('textarea') &&
                        getInputFieldGroup(i).get('type')?.value !== 'textarea'
                      "
                      >Textarea (Chat Input)</mat-option
                    >
                    <mat-option
                      value="input2"
                      [disabled]="
                        hasFieldType('input2') &&
                        getInputFieldGroup(i).get('type')?.value !== 'input2'
                      "
                      >Input2 (Reference Upload)</mat-option
                    >
                  </mat-select>
                </mat-form-field>
              </div>

              <div class="form-field-container col-4">
                <label class="form-field-label">Label</label>
                <mat-form-field class="gingr-input-wrapper w-full" appearance="outline">
                  <input matInput formControlName="label" placeholder="Field label" />
                </mat-form-field>
              </div>

              <div class="form-field-container col-4">
                <label class="form-field-label">Order</label>
                <mat-form-field class="gingr-input-wrapper w-full" appearance="outline">
                  <input matInput type="number" formControlName="order" placeholder="Order" />
                </mat-form-field>
              </div>
            </div>

            <div class="row">
              <div class="form-field-container col-12">
                <label class="form-field-label">Description</label>
                <mat-form-field class="gingr-input-wrapper w-full" appearance="outline">
                  <textarea
                    matInput
                    formControlName="description"
                    placeholder="Field description"
                    rows="2"
                  ></textarea>
                </mat-form-field>
              </div>
            </div>

            <div class="row">
              <div class="form-field-container col-3">
                <mat-checkbox formControlName="show">Show</mat-checkbox>
              </div>
              <div class="form-field-container col-3">
                <mat-checkbox formControlName="required">Required</mat-checkbox>
              </div>
            </div>

            <!-- Upload field specific options (input1 and input2) -->
            @if (
              getInputFieldGroup(i).get('type')?.value === 'input1' ||
              getInputFieldGroup(i).get('type')?.value === 'input2'
            ) {
              <div class="row">
                <div class="form-field-container col-6">
                  <label class="form-field-label">Button Text</label>
                  <mat-form-field class="gingr-input-wrapper w-full" appearance="outline">
                    <input matInput formControlName="buttonText" placeholder="Upload button text" />
                  </mat-form-field>
                </div>
                <div class="form-field-container col-6">
                  <label class="form-field-label">Required Files</label>
                  <mat-form-field class="gingr-input-wrapper w-full" appearance="outline">
                    <input
                      matInput
                      type="number"
                      formControlName="maxFiles"
                      placeholder="Number of required files"
                      min="0"
                    />
                  </mat-form-field>
                </div>
              </div>
            }

            <!-- Textarea field specific UI toggles -->
            @if (getInputFieldGroup(i).get('type')?.value === 'textarea') {
              <div class="m-t-16">
                <h6 class="mat-h6 m-b-8">Textarea UI Options</h6>
                <div class="row">
                  <div class="form-field-container col-4">
                    <mat-checkbox formControlName="showFilters">Show Filters</mat-checkbox>
                  </div>
                  <div class="form-field-container col-4">
                    <mat-checkbox formControlName="showSourceSelector"
                      >Show Source Selector</mat-checkbox
                    >
                  </div>
                  <div class="form-field-container col-4">
                    <mat-checkbox formControlName="showUpload">Show Upload</mat-checkbox>
                  </div>
                </div>
                <div class="row">
                  <div class="form-field-container col-4">
                    <mat-checkbox formControlName="showFolderKeywords"
                      >Show Folder Keywords</mat-checkbox
                    >
                  </div>
                  <div class="form-field-container col-4">
                    <mat-checkbox formControlName="showAvatarSwitcher"
                      >Show Avatar Switcher</mat-checkbox
                    >
                  </div>
                  <div class="form-field-container col-4">
                    <mat-checkbox formControlName="showVoiceOption">Show Voice Option</mat-checkbox>
                  </div>
                </div>
              </div>
            }
          </div>
        }
      </div>
    </div>
  </div>

  @if (!hideButtons) {
    <div class="d-flex align-items-center justify-content-end m-t-16">
      <button
        class="action-button"
        mat-raised-button
        color="accent"
        [disabled]="botEditorForm.invalid"
        (click)="saveBotConfig()"
      >
        Save
      </button>
      <button
        class="action-button"
        mat-raised-button
        color="accent"
        (click)="cancelCallback.emit()"
      >
        Cancel
      </button>
    </div>
  }
</form>
